import {
  comparePPartsDesc,
  orgFromFirestore,
  pPartFromFirestore,
  projectFromFirestore,
  travelerFromFirestore,
} from '@parallel-fluidics/shared/models';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  where,
} from 'firebase/firestore';

export const useGetOrgById = (orgId) => useQuery({
  queryKey: ['getOrgData', orgId],
  queryFn: async () => {
    const orgDocRef = doc(getFirestore(), 'organizations', orgId);
    const orgDoc = await getDoc(orgDocRef);
    if (orgDoc.exists()) {
      return orgFromFirestore(orgDoc);
    }
    throw new Error('Organization not found');
  },
  enabled: !!orgId,
  staleTime: 10 * 60 * 1000,
});

export const useGetProjectById = (projectId, disabled = false) => useQuery({
  queryKey: ['getProjectData', projectId],
  queryFn: async () => {
    const projectQuery = query(
      collection(getFirestore(), 'projects'),
      where('name', '==', projectId),
      limit(1),
    );
    const querySnapshot = await getDocs(projectQuery);
    if (!querySnapshot.empty) {
      const firstDoc = querySnapshot.docs[0];
      return projectFromFirestore(firstDoc);
    }
    throw new Error('Project not found');
  },
  enabled: !!projectId && !disabled,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetProjectByDocId = (projectDocId, disabled = false) => useQuery({
  queryKey: ['getProjectDocData', projectDocId],
  queryFn: async () => {
    const projectDocRef = doc(getFirestore(), 'projects', projectDocId);
    const projectDoc = await getDoc(projectDocRef);
    if (projectDoc.exists()) {
      return projectFromFirestore(projectDoc);
    }
    throw new Error('Project not found');
  },
  enabled: !!projectDocId && !disabled,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetProjectParts = ({
  projectDocId,
  type = null,
  excludeTypes = [],
  status = null,
  disabled = false,
}) => useQuery({
  queryKey: ['getProjectParts', projectDocId, type, excludeTypes, status],
  queryFn: async () => {
    const partsQuery = query(
      collection(getFirestore(), 'projects', projectDocId, 'parts'),
      ...(type ? [where('type', '==', type)] : []),
    );
    const partsSnapshot = await getDocs(partsQuery);

    // A project with no parts is an answer, not a failure — pickers show
    // their empty state (and Assemble its "no BOM" warning) instead of
    // staying in an error limbo.
    return partsSnapshot.docs
      .map((partDoc) => pPartFromFirestore(partDoc))
      // Status filters client-side: legacy docs missing the field would
      // be wrongly dropped by a server-side where — the converter
      // defaults them to Active. excludeTypes joins it there to keep the
      // query shape (and its cache entries) simple.
      .filter((part) => (!status || part.status === status)
        && !excludeTypes.includes(part.type))
      .sort(comparePPartsDesc);
  },
  enabled: !!projectDocId && !disabled,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetPartById = (partId) => useQuery({
  queryKey: ['getPartData', partId],
  queryFn: async () => {
    const partDocRef = doc(getFirestore(), 'parts', partId);
    const partDoc = await getDoc(partDocRef);
    if (partDoc.exists()) {
      return {
        id: partDoc.id,
        ...partDoc.data(),
      };
    }
    throw new Error('Part not found');
  },
  enabled: !!partId,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetOrderById = (orderId) => useQuery({
  queryKey: ['getOrderData', orderId],
  queryFn: async () => {
    const orderDocRef = doc(getFirestore(), 'orders', orderId);
    const orderDoc = await getDoc(orderDocRef);
    if (orderDoc.exists()) {
      return {
        id: orderDoc.id,
        ...orderDoc.data(),
      };
    }
    throw new Error('Order not found');
  },
  enabled: !!orderId,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetOrdersByProjectId = (projectId) => useQuery({
  queryKey: ['getOrdersByProjectId', projectId],
  queryFn: async () => {
    const ordersQuery = query(
      collection(getFirestore(), 'orders'),
      where('projectIds', 'array-contains', projectId),
    );
    const snapshot = await getDocs(ordersQuery);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
  },
  enabled: !!projectId,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetTravelersByIds = (travelerIds) => useQuery({
  queryKey: ['getTravelersData', travelerIds],
  queryFn: async () => {
    const chunks = [];
    const chunkSize = 30; // Firestore 'in' queries support up to 30 values
    for (let i = 0; i < travelerIds.length; i += chunkSize) {
      chunks.push(travelerIds.slice(i, i + chunkSize));
    }
    const travelers = [];
    await Promise.all(
      chunks.map(async (chunk) => {
        const q = query(
          collection(getFirestore(), 'travelers'),
          where(documentId(), 'in', chunk),
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach((d) => {
          travelers.push(travelerFromFirestore(d));
        });
      }),
    );
    return travelers;
  },
  enabled: !!travelerIds && travelerIds.length > 0,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export const useGetTravelerById = (travelerId) => useQuery({
  queryKey: ['getTravelerData', travelerId],
  queryFn: async () => {
    const travelerDocRef = doc(getFirestore(), 'travelers', travelerId);
    const travelerDoc = await getDoc(travelerDocRef);
    if (travelerDoc.exists()) {
      return travelerFromFirestore(travelerDoc);
    }
    throw new Error('Traveler not found');
  },
  enabled: !!travelerId,
  staleTime: 10 * 60 * 1000, // Cache data for 10 minutes
});

export default useGetOrgById;
